import React, { useMemo, useState } from 'react';
import { Alert, Button, Grid2 } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { DatePickerWithInput, SelectCheckbox, SelectComponent } from '../Input';

type SelectCheckboxValue = {
  id: string | number;
  name: string;
};

type onSubmitProps = {
  fromDateTime: string;
  toDateTime: string;
  columns: SelectCheckboxValue[];
};

interface ExportFormProps {
  onSubmit: (props: onSubmitProps) => void;
  onClose: () => void;
  isRangePicker?: boolean;
  downloadOldAlert?: boolean;
}

const ExportForm: React.FC<ExportFormProps> = ({ onSubmit, onClose, isRangePicker = false, downloadOldAlert = true }) => {
  const today = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const { t: translate } = useTranslation();

  const initialMonth = today.getMonth();
  const [fromDateTime, setFromDateTime] = useState<string>(today.toDateString());
  const [toDateTime, setToDateTime] = useState<string>(new Date(today.setMonth(today.getMonth() + 1)).toDateString());
  const [month, setMonth] = useState<number>(initialMonth);
  const [columns, setColumns] = useState<SelectCheckboxValue[]>([]);

  const values = {
    fromDateTime: '',
    toDateTime: '',
    columns: [],
    month: month,
  };

  const columnOptions = [
    { id: 0, name: 'Seleccionar Todos' },
    { id: 1, name: 'Codigo Unico' },
    { id: 2, name: 'Codigo De Reserva' },
    { id: 3, name: 'Fecha De Reserva' },
    { id: 4, name: 'Nombre Triper' },
    { id: 5, name: 'Email Triper' },
    { id: 6, name: 'Zona Horaria' },
    { id: 7, name: 'Fecha De Actividad' },
    { id: 8, name: 'Tour Operador' },
    { id: 9, name: 'Tickets' },
    { id: 10, name: 'Fuente' },
    { id: 11, name: 'Host' },
    { id: 12, name: 'Actividad' },
    { id: 13, name: 'Monto Total' },
    { id: 14, name: 'Monto Pagado' },
    { id: 15, name: 'Id De Pago' },
    { id: 16, name: 'Host Seller' },
    { id: 17, name: 'Status' },
  ];

  const handleSelectColumn = (selectedColumns: SelectCheckboxValue[]) => {
    if (!selectedColumns) return;
    const selectAllChecked = selectedColumns?.find((col) => col.id === 0);
    const isSelectAllInCurrentColumns  = columns?.find((col) => col.id === 0);
    const totalSelectableColumns = columnOptions.filter((col) => col.id !== 0).length;
    const selectedColumnsCount = selectedColumns.filter((col) => col.id !== 0).length;

    if (selectAllChecked && !isSelectAllInCurrentColumns ) {
      setColumns(columnOptions);
    } else if (isSelectAllInCurrentColumns  && !selectAllChecked) {
      setColumns([]);
    } else if (isSelectAllInCurrentColumns  && selectAllChecked && totalSelectableColumns !== selectedColumnsCount) {
      setColumns(selectedColumns.filter((col) => col.id !== 0));
    } else if (totalSelectableColumns === selectedColumnsCount && !selectAllChecked) {
      setColumns(columnOptions);
    } else {
      setColumns(selectedColumns);
    }
  };

  const getMonthDates = (month: number) => {
    const year = today.getFullYear();
    const startOfMonth = new Date(year, month, 1);
    const endOfMonth = new Date(year, month + 1, 0);

    setFromDateTime(startOfMonth.toDateString());
    setToDateTime(endOfMonth.toDateString());
  };

  const handleMonthChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = Number(event.target?.value);
    setMonth(value);
    getMonthDates(value);
  };

  const monthOptions = Array.from({ length: 12 }, (_, index) => ({
    id: index,
    name: translate(`coreUi.months.${index}`),
  }));

  const validateColumns = (value: SelectCheckboxValue[]) => {
    return value.length > 0 ? undefined : translate('coreUi.validation.required');
  };
  return (
    <>
      <Grid2 py={2}>
        <Form
          onSubmit={() => {}}
          initialValues={useMemo(() => values, [])}
          render={({ handleSubmit }) => (
            <form id="form" onSubmit={handleSubmit}>
              <Grid2 container flexDirection="column" spacing={3}>
                {isRangePicker ? (
                  <>
                    <Grid2 width="100%">
                      <DatePickerWithInput
                        label={translate('coreUi.exportForm.label.fromDateTime')}
                        value={fromDateTime}
                        onChange={(date) => {
                          if (!Date.parse(date)) {
                            return;
                          }
                          setFromDateTime(date.toDateString());
                        }}
                      />
                    </Grid2>
                    <Grid2 width="100%">
                      <DatePickerWithInput
                        label={translate('coreUi.exportForm.label.toDateTime')}
                        value={toDateTime}
                        onChange={(date) => {
                          if (!Date.parse(date)) {
                            return;
                          }
                          setToDateTime(date.toDateString());
                        }}
                      />
                    </Grid2>
                  </>
                ) : (
                  <Grid2 width="100%">
                    <Field name="month" component={SelectComponent} label={translate('coreUi.exportForm.label.month')} onFieldChange={handleMonthChange} values={monthOptions} value={month} labelId="month" style={{ width: '100%' }} />
                  </Grid2>
                )}
                <Grid2 width="100%">
                  <Field name="columns" component={SelectCheckbox} label={translate('coreUi.exportForm.label.columns')} onChange={handleSelectColumn} options={columnOptions} values={columns} labelId="columns" required validate={validateColumns} />
                </Grid2>
              </Grid2>
              <Grid2 hidden={!downloadOldAlert} marginTop={'25px'}>
                <Alert style={{ fontSize: '11px' }} severity="info">
                  {translate('coreUi.bookingForm.exportOldBookings')}
                </Alert>
              </Grid2>
              <Grid2 container width="100%" mt={4} justifyContent="flex-end">
                <Button onClick={onClose}>{translate('coreUi.exportForm.cancel')}</Button>
                <Button
                  onClick={() => {
                    const selectAllChecked = columns?.find((col) => col.id === 0);
                    let columnsToSend = columns;
                    if (selectAllChecked) {
                      columnsToSend = columns.filter((col) => col.id !== 0);
                    }

                    onSubmit({
                      fromDateTime,
                      toDateTime,
                      columns: columnsToSend,
                    });
                  }}>
                  {translate('coreUi.exportForm.export')}
                </Button>
              </Grid2>
            </form>
          )}
        />
      </Grid2>
    </>
  );
};

export default ExportForm;
